import ApisServiceApp from './ApisServiceApp'
import { apiUrl } from '@/config'
import { tokenStorage } from '@/services/tokenStorage'
import { apiRequest } from '@/utils/apiRequestUtils'
import { banHangListParamsFromQueryString } from '@/utils/banHangListQueryUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'

const PREFIX = '/dat-hang-ncc'

const blobResponseConfig = { responseType: 'blob' as const }

const parseBlobErrorIfJson = async (blob: Blob): Promise<never> => {
  const text = await blob.text()
  try {
    const j = JSON.parse(text) as { message?: string }
    throw new Error(j?.message || 'Yeu cau that bai')
  } catch (e) {
    if (e instanceof SyntaxError) throw new Error(text.trim().slice(0, 240) || 'Yeu cau that bai')
    throw e
  }
}

const ensureExcelBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type && blob.type.includes('application/json')) await parseBlobErrorIfJson(blob)
  return blob
}

const resolveApiBase = (): string => {
  const fromEnv = (apiUrl || '').replace(/\/$/, '')
  const fromAxios = String(ApisServiceApp.defaults.baseURL || '').replace(/\/$/, '')
  return fromEnv || fromAxios
}

export const fetchDatHangNccList = (search: string): Promise<PagedListResult<Record<string, unknown>>> => {
  const params = banHangListParamsFromQueryString(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-dat-hang-ncc`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return { data: data as Record<string, unknown>[], paging }
    }),
  )
}

export const xuatDatHangNccTongQuanBlob = async (search?: string): Promise<Blob> => {
  const params = search ? banHangListParamsFromQueryString(search) : {}
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-tong-quan-dat-hang-ncc`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const xuatDatHangNccChiTietBlob = async (search?: string): Promise<Blob> => {
  const params = search ? banHangListParamsFromQueryString(search) : {}
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-chi-tiet-dat-hang-ncc`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const taiTemplateDatHangNccBlob = async (): Promise<Blob> => {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/tai-template-dat-hang-ncc`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const taoDatHangNcc = (body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/tao-dat-hang-ncc`, body))

export const fetchDatHangNccDetail = (id: string | number) =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-dat-hang-ncc`).then((res: unknown) => ({
      data: normalizeDetailResponse(res as { data?: Record<string, unknown> }),
    })),
  )

export const capNhatDatHangNcc = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-dat-hang-ncc`, body))

export const xoaDatHangNcc = (id: string | number) =>
  apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-dat-hang-ncc`))

export const chuyenTrangThaiDatHangNcc = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-dat-hang-ncc`, body))

export const saoChepDatHangNcc = (id: string | number) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/sao-chep-dat-hang-ncc`, {}))

export const huyDatHangNcc = (id: string | number) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/huy-dat-hang-ncc`, {}))

export const ketThucDatHangNcc = (id: string | number) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/ket-thuc-dat-hang-ncc`, {}))

export const nhapHangHoaTuExcelDatHangNcc = async (id: string | number, file: File): Promise<unknown> => {
  const base = resolveApiBase()
  if (!base) throw new Error('Chưa cấu hình API base URL')
  const formData = new FormData()
  formData.append('file', file)
  const token = tokenStorage.getAccessToken()
  const res = await fetch(`${base}${PREFIX}/${id}/nhap-hang-hoa-tu-excel-dat-hang-ncc`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = (await res.json()) as { message?: string }
      if (j?.message) msg = j.message
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  const text = await res.text()
  if (!text) return {}
  try {
    return JSON.parse(text) as unknown
  } catch {
    return {}
  }
}
